import type { AppointmentListItem } from '@/hooks/useAppointments';
import { deriveVisitPaymentState } from '@/lib/appointmentPaymentState';
import { resolveAppointmentStatus } from '@/utils/appointmentStatus';
import { type CalendarLifecycleStatus } from '@/utils/appointmentCalendarStyle';

/** Extensible billing method. Calendar UI must not switch on specific methods. */
export type CalendarBillingMethod = string | null;

/**
 * Extensible settlement status. Known values used by current data include:
 * none | pending | submitted | unpaid | partially_paid | paid | settled | rejected | cancelled
 */
export type CalendarBillingStatus = string;

export type CalendarBillingPresentation = {
  /** How the appointment is financially handled (invoice, klippekort, insurance, …). */
  method: CalendarBillingMethod;
  /** Where the financial process currently is. */
  status: CalendarBillingStatus;
  /** Invoice issued, klippekort punched, or equivalent financial processing. */
  processed: boolean;
  /** Fully paid / punched / settled — drives the green receipt icon. */
  settled: boolean;
  /** i18n key under `appointments.card` for the billing accessibility label. */
  labelKey: string;
};

/** Visual state for the single receipt icon — financial state, not payment method. */
export type CalendarBillingIconTone = 'muted' | 'neutral' | 'settled' | 'attention';

export type CalendarJournalAttention = 'needed' | 'draft' | null;

export interface CalendarJournalMeta {
  attention: CalendarJournalAttention;
  entryId: string | null;
  startedAt: string | null;
  /** Set only when the clinician has signed off the note. */
  finalizedAt: string | null;
}

export interface LinkedCalendarInvoice {
  appointment_id: string;
  invoice_id: string;
  invoice_number: string | null;
  status: string;
  payment_method: string | null;
}

export interface CalendarJournalEntryMeta {
  id: string;
  appointment_id: string;
  content_md: string;
  created_at: string;
  updated_at: string;
  finalized_at?: string | null;
  is_primary?: boolean | null;
  discarded_at?: string | null;
}

export type CalendarBillingSource = {
  payment_method?: string | null;
  payment_status?: string | null;
  visit_status?: string | null;
  invoiceStatus?: string | null;
  invoicePaymentMethod?: string | null;
  /** When set with a slot time, a covered future visit is labelled Forudbetalt. */
  status?: string | null;
  starts_at?: string | null;
  ends_at?: string | null;
};

const ISSUED_INVOICE_STATUSES = new Set(['issued', 'partial', 'overdue', 'paid', 'sent']);
const PAID_INVOICE_STATUSES = new Set(['paid']);
const PARTIAL_INVOICE_STATUSES = new Set(['partial']);
const CANCELLED_INVOICE_STATUSES = new Set(['cancelled', 'void', 'voided']);
const REJECTED_INVOICE_STATUSES = new Set(['rejected', 'failed', 'refunded']);

function normalizeBillingMethod(raw: string | null | undefined): CalendarBillingMethod {
  if (!raw) return null;
  if (raw === 'punch_card') return 'klippekort';
  if (raw === 'stripe' || raw === 'kort') return 'card';
  return raw;
}

function paidLabelKey(method: CalendarBillingMethod): string {
  if (method === 'clinio_pay') return 'billing_generic';
  if (method === 'card' || method === 'mobilepay' || method === 'klippekort') {
    return 'billing_paid_with_method';
  }
  return method ? 'billing_generic' : 'billing_paid';
}

function isPunchCardSettled(input: CalendarBillingSource): boolean {
  const method = input.payment_method ?? input.invoicePaymentMethod;
  if (method !== 'punch_card') return false;
  return (
    input.payment_status === 'paid' ||
    (Boolean(input.invoiceStatus) && PAID_INVOICE_STATUSES.has(input.invoiceStatus as string))
  );
}

function invoiceSettlementStatus(invoiceStatus: string): CalendarBillingStatus {
  if (PAID_INVOICE_STATUSES.has(invoiceStatus)) return 'paid';
  if (PARTIAL_INVOICE_STATUSES.has(invoiceStatus)) return 'partially_paid';
  if (CANCELLED_INVOICE_STATUSES.has(invoiceStatus)) return 'cancelled';
  if (REJECTED_INVOICE_STATUSES.has(invoiceStatus)) return 'rejected';
  if (ISSUED_INVOICE_STATUSES.has(invoiceStatus)) return 'unpaid';
  return invoiceStatus || 'pending';
}

function genericLabelKey(): string {
  return 'billing_generic';
}

/**
 * Normalize billing for calendar presentation. Method-specific rules live here,
 * not in appointment card UI.
 *
 * A covered visit that has not been completed is Forudbetalt. Paying does
 * not change the appointment lifecycle.
 */
export function getCalendarBillingPresentation(
  input: CalendarBillingSource,
  options?: { now?: Date },
): CalendarBillingPresentation {
  return withPrepaidLabel(input, presentCalendarBilling(input), options?.now);
}

function presentCalendarBilling(input: CalendarBillingSource): CalendarBillingPresentation {
  if (isPunchCardSettled(input)) {
    return {
      method: 'klippekort',
      status: 'settled',
      processed: true,
      settled: true,
      labelKey: 'billing_paid_klippekort',
    };
  }

  if (input.invoiceStatus) {
    const status = invoiceSettlementStatus(input.invoiceStatus);
    const method = normalizeBillingMethod(input.invoicePaymentMethod) ?? 'invoice';
    if (
      CANCELLED_INVOICE_STATUSES.has(input.invoiceStatus) ||
      REJECTED_INVOICE_STATUSES.has(input.invoiceStatus)
    ) {
      return {
        method,
        status,
        processed: false,
        settled: false,
        labelKey: method === 'invoice' ? 'billing_invoice_cancelled' : genericLabelKey(),
      };
    }
    if (
      ISSUED_INVOICE_STATUSES.has(input.invoiceStatus) ||
      status === 'paid' ||
      status === 'partially_paid'
    ) {
      if (status === 'paid') {
        return {
          method,
          status,
          processed: true,
          settled: true,
          labelKey: method === 'invoice' ? 'billing_invoice_paid' : genericLabelKey(),
        };
      }
      if (status === 'partially_paid') {
        return {
          method,
          status,
          processed: true,
          settled: false,
          labelKey: method === 'invoice' ? 'billing_invoice_partial' : genericLabelKey(),
        };
      }
      return {
        method,
        status: 'unpaid',
        processed: true,
        settled: false,
        labelKey: method === 'invoice' ? 'billing_invoice_unpaid' : genericLabelKey(),
      };
    }
  }

  const method = normalizeBillingMethod(input.payment_method);
  if (input.payment_status === 'paid') {
    return {
      method: method ?? 'other',
      status: 'paid',
      processed: true,
      settled: true,
      labelKey: method === 'invoice' ? 'billing_invoice_paid' : paidLabelKey(method ?? 'other'),
    };
  }

  if (input.payment_status === 'pending' || input.payment_status === 'processing') {
    return {
      method,
      status: 'pending',
      processed: false,
      settled: false,
      labelKey: 'billing_not_processed',
    };
  }

  return {
    method,
    status: 'none',
    processed: false,
    settled: false,
    labelKey: 'billing_not_processed',
  };
}

function withPrepaidLabel(
  input: CalendarBillingSource,
  presentation: CalendarBillingPresentation,
  now?: Date,
): CalendarBillingPresentation {
  if (!input.starts_at && !input.ends_at) return presentation;
  const lifecycle = resolveAppointmentStatus({
    status: input.status,
    visitStatus: input.visit_status,
    startsAt: input.starts_at ?? undefined,
    endsAt: input.ends_at ?? undefined,
    now,
  });
  const state = deriveVisitPaymentState({
    lifecycle,
    invoiceStatus: input.invoiceStatus,
    paymentStatus: input.payment_status,
    punchCardHeld: input.payment_method === 'punch_card' && input.payment_status === 'paid',
  });
  if (state === 'refunded') {
    return {
      ...presentation,
      status: 'refunded',
      settled: false,
      processed: true,
      labelKey: 'billing_refunded',
    };
  }
  if (state !== 'prepaid') return presentation;
  return { ...presentation, status: 'prepaid', labelKey: 'billing_prepaid' };
}

export function getCalendarBillingIconTone(
  billing: CalendarBillingPresentation,
): CalendarBillingIconTone {
  if (billing.settled) return 'settled';
  if (billing.status === 'rejected' || billing.status === 'cancelled') return 'attention';
  if (
    billing.processed ||
    billing.status === 'submitted' ||
    billing.status === 'pending' ||
    billing.status === 'unpaid' ||
    billing.status === 'partially_paid'
  ) {
    return 'neutral';
  }
  return 'muted';
}

/**
 * Calendar appointment status. Billing is ignored — payment never finishes
 * a visit. Callers still pass billing so existing screens stay one mapping.
 */
export function getCalendarAppointmentStatus(
  appointment: Pick<AppointmentListItem, 'visit_status' | 'status' | 'starts_at' | 'ends_at'>,
  _billing?: CalendarBillingPresentation,
  now?: Date,
): CalendarLifecycleStatus {
  return resolveAppointmentStatus({
    status: appointment.status,
    visitStatus: appointment.visit_status,
    startsAt: appointment.starts_at,
    endsAt: appointment.ends_at,
    now,
  });
}

export function journalIsDraft(contentMd: string | null | undefined): boolean {
  return !contentMd?.trim();
}

/**
 * Dark rail only when the clinician has finalized the note.
 * Missing journal, linked draft, and non-empty content are all false.
 */
export function calendarJournalIsCompleted(
  journal: CalendarJournalMeta | null | undefined,
): boolean {
  const raw = journal?.finalizedAt;
  if (typeof raw !== 'string' || raw.trim() === '') return false;
  return Number.isFinite(Date.parse(raw));
}

function journalMeta(
  attention: CalendarJournalAttention,
  entryId: string | null,
  startedAt: string | null,
  finalizedAt: string | null = null,
): CalendarJournalMeta {
  return { attention, entryId, startedAt, finalizedAt };
}

export function getCalendarJournalAttention(input: {
  appointment: Pick<AppointmentListItem, 'patient_id' | 'status' | 'visit_status' | 'starts_at'>;
  journal: CalendarJournalEntryMeta | null;
  now?: Date;
}): CalendarJournalMeta {
  if (input.journal) {
    const finalizedAt = input.journal.finalized_at ?? null;
    if (finalizedAt) {
      return journalMeta(null, input.journal.id, input.journal.created_at, finalizedAt);
    }
    return journalMeta('draft', input.journal.id, input.journal.created_at);
  }

  const { appointment } = input;
  if (!appointment.patient_id) {
    return journalMeta(null, null, null);
  }
  if (appointment.status === 'cancelled') {
    return journalMeta(null, null, null);
  }

  const now = input.now ?? new Date();
  const hasStartedVisit = Boolean(appointment.visit_status);
  const isPast = new Date(appointment.starts_at).getTime() <= now.getTime();
  const isTerminal = appointment.status === 'completed' || appointment.status === 'no_show';

  if (hasStartedVisit || isPast || isTerminal) {
    return journalMeta('needed', null, null);
  }

  return journalMeta(null, null, null);
}

export function pickCanonicalAppointmentJournal(
  rows: CalendarJournalEntryMeta[],
  appointmentId: string,
): CalendarJournalEntryMeta | null {
  const candidates = rows.filter(
    (row) => row.appointment_id === appointmentId && !row.discarded_at,
  );
  if (candidates.length === 0) return null;
  const primary = candidates.filter((row) => row.is_primary !== false);
  const pool = primary.length > 0 ? primary : candidates;
  const finalized = pool.filter((row) => row.finalized_at);
  const ranked = (finalized.length > 0 ? finalized : pool).slice().sort((a, b) => {
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });
  return ranked[0] ?? null;
}

export function calendarStatusLabelKey(status: CalendarLifecycleStatus): string {
  return `appointments.legend.${status}`;
}
